import { useRef, useEffect } from "react";
import { Row, Button, Typography } from "antd";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import moment from "moment";
import _get from "lodash/get";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import BackButton from "components/BackButton";
import {
  TextInput,
  ImageUpload,
  DatePicker,
  TextEditor,
  Select,
} from "components/common";
import {
  getContestDetailRequest,
  updateContestRequest,
  updateVotingLogicRequest,
} from "providers/ContestProvider/slice";
import Helper from "utils/helpers";
import "./styles.less";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Tên cuộc thi là bắt buộc"),
  bannerImage: Yup.object().nullable().required("Banner là bắt buộc"),
  bannerContestSquare: Yup.object().nullable(),
  startDate: Yup.date().required("Ngày bắt đầu là bắt buộc"),
  endDate: Yup.date()
    .min(Yup.ref("startDate"), "Ngày kết thúc phải sau ngày bắt đầu")
    .required("Ngày kết thúc là bắt buộc"),
  description: Yup.string().required("Mô tả là bắt buộc"),
  rules: Yup.string().required("Thể lệ là bắt buộc"),
  routeBanner: Yup.object().nullable(),
  logoContest: Yup.object().nullable(),
  timelineContest: Yup.object().nullable(),
  registerFormLink: Yup.string().url("URL không hợp lệ").nullable(),
});

const ContestEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const formikRef = useRef();

  const contestDetail = useSelector(
    (state) => state.contestSlice.contestDetail
  );
  const isLoading = useSelector((state) => state.contestSlice.isLoading);

  useEffect(() => {
    console.log(id, "contestId from URL");
    if (id) {
      dispatch(getContestDetailRequest(id));
    }
  }, [id, dispatch]);

  // Debug: Log contest detail
  useEffect(() => {
    console.log("Contest Detail:", contestDetail);
  }, [contestDetail]);

  const handleUpdateContest = (values) => {
    const contestData = {
      name: values.name,
      bannerImage: values.bannerImage?.url || values.bannerImage,
      bannerContestSquare: values.bannerContestSquare?.url || values.bannerContestSquare || null,
      startDate: moment(values.startDate).toISOString(),
      endDate: moment(values.endDate).toISOString(),
      description: values.description,
      rules: values.rules,
      routeBanner: values.routeBanner?.url || values.routeBanner || null,
      logoContest: values.logoContest?.url || values.logoContest || null,
      timelineContest: values.timelineContest?.url || values.timelineContest || null,
      registerFormLink: values.registerFormLink || null,
    };

    dispatch(
      updateContestRequest({
        contestId: id,
        contestData,
      })
    )
      .then(async () => {
        // Update voting logic separately if it has changed
        if (values.votingLogic && values.votingLogic !== contestDetail.votingLogic) {
          try {
            await dispatch(updateVotingLogicRequest({
              contestId: id,
              votingLogic: values.votingLogic
            }));
          } catch (error) {
            console.error("Update voting logic error:", error);
            // Contest was updated but voting logic update failed
            Helper.toastr(
              "Cảnh báo",
              "Cuộc thi đã được cập nhật nhưng không thể cập nhật logic bỏ phiếu",
              "warning"
            );
          }
        }
        
        Helper.toastr(
          "Cập nhật cuộc thi thành công",
          "Cuộc thi đã được cập nhật thành công",
          "success"
        );
        navigate(`/voting/contest_list`, {
          state: { canGoBack: true },
          replace: true,
        });
      })
      .catch((error) => {
        console.error("Update contest error:", error);
        Helper.toastr(
          "Cập nhật cuộc thi thất bại",
          "Có lỗi xảy ra khi cập nhật cuộc thi. Vui lòng thử lại.",
          "error"
        );
      });
  };

  const getInitialValues = () => {
    console.log("Getting initial values, contestDetail:", contestDetail);

    if (!contestDetail || Object.keys(contestDetail).length === 0) {
      return {
        name: "",
        bannerImage: null,
        bannerContestSquare: null,
        startDate: null,
        endDate: null,
        description: "",
        rules: "",
        routeBanner: null,
        logoContest: null,
        timelineContest: null,
        registerFormLink: "",
        votingLogic: 1,
      };
    }

    const initialValues = {
      name: contestDetail.name || "",
      bannerImage: contestDetail.bannerImage
        ? {
            url: contestDetail.bannerImage,
            objectId: contestDetail.bannerImage,
          }
        : null,
      bannerContestSquare: contestDetail.bannerContestSquare
        ? {
            url: contestDetail.bannerContestSquare,
            objectId: contestDetail.bannerContestSquare,
          }
        : null,
      startDate: contestDetail.startDate
        ? moment(contestDetail.startDate)
        : null,
      endDate: contestDetail.endDate ? moment(contestDetail.endDate) : null,
      description: contestDetail.description || "",
      rules: contestDetail.rules || "",
      routeBanner: contestDetail.routeBanner
        ? {
            url: contestDetail.routeBanner,
            objectId: contestDetail.routeBanner,
          }
        : null,
      logoContest: contestDetail.logoContest
        ? {
            url: contestDetail.logoContest,
            objectId: contestDetail.logoContest,
          }
        : null,
      timelineContest: contestDetail.timelineContest
        ? {
            url: contestDetail.timelineContest,
            objectId: contestDetail.timelineContest,
          }
        : null,
      registerFormLink: contestDetail.registerFormLink || "",
      votingLogic: contestDetail.votingLogic || 1,
    };

    console.log("Initial values:", initialValues);
    return initialValues;
  };

  // Don't render form until we have contest data
  if (!contestDetail || Object.keys(contestDetail).length === 0) {
    return (
      <div className="contest-edit-wrapper">
        <Row className="contest-edit-header" justify="space-between">
          <Row>
            <BackButton />
            <Typography.Title level={4}>Chỉnh sửa cuộc thi</Typography.Title>
          </Row>
        </Row>
        <div className="edit-form-wrapper">
          <div style={{ padding: "24px", textAlign: "center" }}>
            {isLoading
              ? "Đang tải dữ liệu..."
              : "Không tìm thấy dữ liệu cuộc thi"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contest-edit-wrapper">
      <Row className="contest-edit-header" justify="start" align="middle">
        <BackButton />
        <Typography.Title level={4}>Chỉnh sửa cuộc thi</Typography.Title>
      </Row>
      <div className="edit-form-wrapper">
        <Formik
          innerRef={formikRef}
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleUpdateContest}
          enableReinitialize
        >
          {({ handleSubmit: formikHandleSubmit, values, setFieldValue }) => (
            <Form>
              <div className="contest-form-container">
                {/* Banner Image */}
                <div className="form-section">
                  <div className="section-title">Banner cuộc thi</div>
                  <div className="upload-note">
                    <small style={{ color: '#666', fontSize: '12px' }}>
                    Banner tỉ lệ 3 ngang 1 dọc (hình chữ nhật)
                    </small>
                  </div>
                  <ImageUpload
                    name="bannerImage"
                    setFieldValue={setFieldValue}
                    required
                    className="contest-banner-upload"
                    disableCrop={true}
                  />
                </div>

                {/* Square Banner Image */}
                <div className="form-section">
                  <div className="section-title">Banner vuông cuộc thi</div>
                  <div className="upload-note">
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Banner tỷ lệ 1:1 (vuông), có thể cắt ảnh để phù hợp
                    </small>
                  </div>
                  <ImageUpload
                    name="bannerContestSquare"
                    setFieldValue={setFieldValue}
                    className="contest-square-banner-upload"
                    aspectRatio={1}
                    cropModalProps={{ width: 600 }}
                  />
                </div>

                {/* Basic Information */}
                <div className="form-section">
                  <div className="section-title">Thông tin cơ bản</div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Tên cuộc thi:</div>
                      <TextInput name="name" size="large" required />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Ngày bắt đầu:</div>
                      <DatePicker
                        name="startDate"
                        size="large"
                        showTime={false}
                        format="DD/MM/YYYY"
                        placeholder="Chọn ngày bắt đầu"
                        required
                      />
                    </div>
                    <div className="form-item">
                      <div className="item-title">Ngày kết thúc:</div>
                      <DatePicker
                        name="endDate"
                        size="large"
                        showTime={false}
                        format="DD/MM/YYYY"
                        placeholder="Chọn ngày kết thúc"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Logic bỏ phiếu:</div>
                      <Select
                        name="votingLogic"
                        size="large"
                        placeholder="Chọn logic bỏ phiếu"
                        options={[
                          { value: 1, label: "Bỏ phiếu hàng ngày (người dùng có thể bỏ phiếu mỗi ngày một lần cho mỗi ứng viên)" },
                          { value: 2, label: "Bỏ phiếu một lần (người dùng chỉ có thể bỏ phiếu một lần cho mỗi ứng viên trong suốt cuộc thi)" }
                        ]}
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Images and Links */}
                <div className="form-section">
                  <div className="section-title">Hình ảnh và liên kết bổ sung</div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Banner lộ trình:</div>
                      <div className="upload-note-small">
                        <small style={{ color: '#666', fontSize: '11px' }}>
                          Ảnh hiển thị với kích thước gốc
                        </small>
                      </div>
                      <ImageUpload
                        name="routeBanner"
                        setFieldValue={setFieldValue}
                        className="contest-additional-upload"
                        disableCrop={true}
                      />
                    </div>
                    <div className="form-item">
                      <div className="item-title">Logo cuộc thi:</div>
                      <div className="upload-note-small">
                        <small style={{ color: '#666', fontSize: '11px' }}>
                          Ảnh hiển thị với kích thước gốc
                        </small>
                      </div>
                      <ImageUpload
                        name="logoContest"
                        setFieldValue={setFieldValue}
                        className="contest-additional-upload"
                        disableCrop={true}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Timeline cuộc thi:</div>
                      <div className="upload-note-small">
                        <small style={{ color: '#666', fontSize: '11px' }}>
                          Ảnh hiển thị với kích thước gốc
                        </small>
                      </div>
                      <ImageUpload
                        name="timelineContest"
                        setFieldValue={setFieldValue}
                        className="contest-additional-upload"
                        disableCrop={true}
                      />
                    </div>
                    <div className="form-item">
                      <div className="item-title">Link form đăng ký:</div>
                      <TextInput 
                        name="registerFormLink" 
                        size="large" 
                        placeholder="https://forms.google.com/register"
                      />
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="form-section">
                  <div className="section-title">Mô tả</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Mô tả cuộc thi:</div>
                      <TextEditor
                        name="description"
                        placeholder="Nhập mô tả chi tiết về cuộc thi..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Rules */}
                <div className="form-section">
                  <div className="section-title">Thể lệ</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Thể lệ cuộc thi:</div>
                      <TextEditor
                        name="rules"
                        placeholder="Nhập thể lệ cuộc thi..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="form-actions">
                  <Button
                    loading={isLoading}
                    size="large"
                    type="primary"
                    onClick={() => formikRef.current?.submitForm()}
                  >
                    Cập nhật
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ContestEdit;
